import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/hooks';
import { createDevRejectionHandler } from '../../../shared/utils';
import { loadCss, loadScript } from '../hooks/useRecordingSessions';

import { markRecordingPlaybackNotFound } from './playbackNotFound';

import './SessionRecording.css';

// Global window type extensions
declare global {
  interface Window {
    initPlayer?: (filename: string) => void;
  }
}

const getCookie = (name: string): string => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    const last = parts.pop();

    return last ? last.split(';').shift() ?? '' : '';
  }

  return '';
};

/**
 * Sanitizes a filename to prevent XSS when used in URLs or passed to external functions.
 * Removes dangerous characters and ensures the filename is safe for URL construction.
 * Note: Returns the sanitized filename WITHOUT encoding, as initPlayer will handle URL encoding.
 */
const sanitizeFilename = (input: string): string | null => {
  if (!input || typeof input !== 'string') {
    return null;
  }

  // Remove any control characters, null bytes, and trim whitespace
  // Filter out control characters (0x00-0x1F) and DEL (0x7F) by checking character codes
  let sanitized = input
    .trim()
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0);

      return code > 31 && code !== 127;
    })
    .join('');

  if (!sanitized) {
    return null;
  }

  // Block dangerous protocols that could lead to XSS
  // Check protocols by constructing the string in a way that doesn't trigger no-script-url
  const getJsProtocol = (): string => {
    const parts = ['java', 'script', ':'];

    return parts.join('');
  };

  const dangerousProtocols = [
    getJsProtocol(),
    'data:',
    'vbscript:',
    'file:',
    'about:',
  ];

  const lowerTrimmed = sanitized.toLowerCase();

  // Check if input starts with any dangerous protocol
  const hasDangerousProtocol = dangerousProtocols.some((protocol) =>
    lowerTrimmed.startsWith(protocol)
  );

  if (hasDangerousProtocol) {
    return null;
  }

  // Block dangerous patterns that could lead to XSS in URL construction
  // Check for patterns that could break out of query parameter context
  // Note: We check for ?&# at the start or in specific contexts, not just anywhere
  // as these could be legitimate filename characters in some systems
  const getJsProtocolPattern = (): RegExp => {
    const parts = ['java', 'script', ':'];

    return new RegExp(parts.join(''), 'i');
  };

  const dangerousPatterns = [
    /^[?&#]/, // Starting with query/fragment/param separators
    /[?&#].*[?&#]/, // Multiple separators (likely injection attempt)
    getJsProtocolPattern(),
    /data:/i,
    /vbscript:/i,
  ];

  // Check if input matches any dangerous pattern
  const matchesDangerousPattern = dangerousPatterns.some((pattern) =>
    pattern.test(sanitized)
  );

  if (matchesDangerousPattern) {
    return null;
  }

  // Remove any HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '');

  // Return sanitized filename without encoding - initPlayer will handle URL encoding
  // This prevents double-encoding issues
  return sanitized;
};

type PlaybackKind = 'playback' | 'past' | '';

interface PlaybackResponse {
  PAST?: string;
  Filename?: string;
  IsMp4Video?: 'true' | 'false';
  LIVE?: string;
  error?: string;
  message?: string;
}

const extractMp4UrlFromPast = (past: unknown): string | null => {
  if (typeof past !== 'string') {
    return null;
  }

  const trimmed = past.trim();

  // Fast path: already a direct URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // If backend returns HTML (<video><source src="...">...), extract the src safely.
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(trimmed, 'text/html');
    const sourceEl = doc.querySelector('source');
    const videoEl = doc.querySelector('video');

    const src =
      sourceEl?.getAttribute('src') ?? videoEl?.getAttribute('src') ?? null;

    if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
      return src;
    }
  } catch {
    // ignore; fall back to regex
  }

  // Conservative regex fallback (only accept http/https src attributes)
  const match = trimmed.match(/src=['"](https?:\/\/[^'"]+)['"]/i);

  return match?.[1] ?? null;
};

const getAllowedEnvHost = (): string | null => {
  try {
    const envDomain =
      process.env.ECM_API_DOMAIN ?? process.env.REACT_APP_ECM_API_DOMAIN ?? '';

    if (!envDomain) {
      return null;
    }

    return new URL(envDomain).host;
  } catch {
    return null;
  }
};

const isAllowedRecordingHost = (
  host: string,
  allowedEnvHost: string | null
): boolean => {
  const currentHost = window.location.host;

  return (
    host === currentHost ||
    (allowedEnvHost != null && host === allowedEnvHost) ||
    host.endsWith('.amazonaws.com') ||
    host.endsWith('.cloudfront.net')
  );
};

const sanitizeMp4Src = (src: unknown): string | null => {
  if (typeof src !== 'string') {
    return null;
  }

  const cleanedSrc = src.trim();

  if (!cleanedSrc) {
    return null;
  }

  try {
    const parsed = new URL(cleanedSrc, window.location.origin);

    // Only allow HTTPS absolute URLs for recorded MP4 playback
    if (parsed.protocol !== 'https:') {
      return null;
    }

    const allowedEnvHost = getAllowedEnvHost();

    if (!isAllowedRecordingHost(parsed.host, allowedEnvHost)) {
      return null;
    }

    return cleanedSrc;
  } catch {
    return null;
  }
};

const playbackCache = new Map<string, PlaybackResponse>();
const playbackInflight = new Map<
  string,
  { promise: Promise<PlaybackResponse>; controller: AbortController }
>();

const PLAYBACK_TIMEOUT_MS = 8000;
const VIDEO_FETCH_ERROR_MESSAGE =
  "We couldn't load this session recording right now. Refresh and try again. If the issue continues, contact your administrator.";

const fetchPlayback = async (id: string): Promise<PlaybackResponse> => {
  const cached = playbackCache.get(id);

  if (cached) {
    return cached;
  }

  const inflight = playbackInflight.get(id);

  if (inflight) {
    return inflight.promise;
  }

  const controller = new AbortController();
  const promise = (async () => {
    const timeoutId = window.setTimeout(() => {
      controller.abort();
    }, PLAYBACK_TIMEOUT_MS);

    const tokenType = getCookie('token_type');
    const accessToken = getCookie('access_token');

    try {
      const response = await fetch(
        `/ECMv6/api/rdpgateway/videoplaybackapi?launchid=${encodeURIComponent(
          id
        )}`,
        {
          method: 'GET',
          headers: {
            Authorization: `${tokenType} ${accessToken}`,
          },
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          markRecordingPlaybackNotFound(id);
          throw new Error('Playback not found');
        }

        throw new Error(VIDEO_FETCH_ERROR_MESSAGE);
      }

      const json = (await response.json()) as PlaybackResponse;

      playbackCache.set(id, json);
      playbackInflight.delete(id);

      return json;
    } catch (err) {
      playbackInflight.delete(id);

      if ((err as { name?: string }).name === 'AbortError') {
        throw new Error(
          'The recording timed out while loading. Please try again.'
        );
      }

      throw err;
    } finally {
      window.clearTimeout(timeoutId);
    }
  })();

  playbackInflight.set(id, { promise, controller });

  return promise;
};

export const prefetchRecordingPlayback = (
  launchId: string | number
): Promise<void> =>
  fetchPlayback(String(launchId)).then(
    () => undefined,
    () => undefined
  );

interface SessionRecordingProps {
  launchId: string | number;
  launchStartDate?: string;
  /**
   * For MP4 playback: seek target in seconds from the start of the launch.
   * Used for timestamp-driven navigation (e.g., timeline timestamp click).
   */
  seekSeconds?: number;
}

/**
 * Renders the appropriate recording viewer (Windows/terminal) for a session.
 */
function SessionRecording(props: SessionRecordingProps) {
  const { launchId, launchStartDate, seekSeconds } = props;

  return (
    <SessionRecordingWindows
      launchId={launchId}
      launchStartDate={launchStartDate}
      seekSeconds={seekSeconds}
    />
  );
}

/**
 * SessionRecording Component
 *
 * Integrates PAM session recording functionality into AuditLogViewer.
 * Supports both live monitoring and recorded session playback.
 */
function SessionRecordingWindows({
  launchId,
  launchStartDate,
  seekSeconds,
}: Pick<
  SessionRecordingProps,
  'launchId' | 'launchStartDate' | 'seekSeconds'
>) {
  const l = useInvertedLocalize();
  const [videoType, setVideoType] = useState<PlaybackKind>('');
  const [filename, setFilename] = useState('');
  const [mp4Src, setMp4Src] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTimedOut, setFetchTimedOut] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const loadingText = l('Loading...');
  const unavailableText = l('No Session Recording Found');
  const mp4VideoRef = useRef<HTMLVideoElement | null>(null);
  const prevMp4SrcRef = useRef<string | null>(null);
  const guacContainerRef = useRef<HTMLDivElement | null>(null);
  const cancelledRef = useRef(false);

  const handlePlaybackResponse = useCallback((json: PlaybackResponse) => {
    // Validate response - check if it's an error response
    if (
      json.error ||
      (json.message && !json.Filename && !json.PAST) ||
      (!json.Filename && !json.PAST && json.IsMp4Video === undefined)
    ) {
      throw new Error(
        json.message ?? json.error ?? 'Invalid response from video playback API'
      );
    }

    // Use HTML5 video for MP4 videos, Guacamole for non-MP4 playback videos
    const pastMp4Url = extractMp4UrlFromPast(json.PAST);
    const filenameVal =
      typeof json.Filename === 'string' ? json.Filename.trim() : '';
    const mp4Candidate = filenameVal.length > 0 ? filenameVal : pastMp4Url;
    const safeMp4Src = sanitizeMp4Src(mp4Candidate);

    // MP4: backend may return either Filename URL, or HTML in PAST containing a <source src="...">
    if (json.IsMp4Video === 'true') {
      if (safeMp4Src) {
        setMp4Src(safeMp4Src);
        setFilename(safeMp4Src);
        setVideoType('past');
      } else {
        setVideoType('');
      }
    } else if (json.IsMp4Video === 'false') {
      // Guacamole playback requires a filename token
      if (filenameVal.length > 0) {
        setMp4Src(null);
        setFilename(filenameVal);
        setVideoType('playback');
      } else {
        setVideoType('');
      }
    } else if (filenameVal.length > 0) {
      // Fallback when IsMp4Video is missing: prefer token-based playback
      setMp4Src(null);
      setFilename(filenameVal);
      setVideoType('playback');
    } else if (pastMp4Url) {
      // Fallback when IsMp4Video is missing: allow direct/embedded MP4 URL
      const safeFallbackSrc = sanitizeMp4Src(pastMp4Url);

      if (safeFallbackSrc) {
        setMp4Src(safeFallbackSrc);
        setFilename(safeFallbackSrc);
        setVideoType('past');
      } else {
        setVideoType('');
      }
    } else {
      setVideoType('');
    }

    setIsLoading(false);
  }, []);

  // Auto-play when an MP4 recording is selected.
  // This runs both for user clicks and programmatic selection (timestamp matching).
  useEffect(() => {
    if (videoType !== 'past' || !mp4Src) {
      return undefined;
    }

    const videoEl = mp4VideoRef.current;

    if (!videoEl) {
      return undefined;
    }

    const prevMp4Src = prevMp4SrcRef.current;
    const shouldReload = prevMp4Src !== mp4Src;

    prevMp4SrcRef.current = mp4Src;

    // Ensure the new <source> is loaded before seeking/playing.
    if (shouldReload) {
      try {
        videoEl.load();
      } catch {
        // ignore
      }
    }

    let didStart = false;

    const safeTargetSeconds = (() => {
      // Explicitly treat null/undefined as "skip seek"; Number(null) === 0 would otherwise seek to start.
      if (seekSeconds == null) {
        return null;
      }

      const raw = Number(seekSeconds);

      return Number.isFinite(raw) && raw >= 0 ? raw : null;
    })();

    const startPlayback = () => {
      if (didStart) {
        return;
      }

      didStart = true;

      // Autoplay may be blocked in some browsers if not considered user-initiated.
      // We intentionally ignore failures and let the user hit Play.
      try {
        videoEl.play().catch(() => {
          // ignore (autoplay may be blocked)
        });
      } catch {
        // ignore
      }
    };

    const seekAndPlay = () => {
      if (safeTargetSeconds != null) {
        try {
          // Avoid seeking exactly to duration (can no-op / pause at end in some browsers).
          const dur = Number(videoEl.duration);
          const clamped =
            Number.isFinite(dur) && dur > 0
              ? Math.min(safeTargetSeconds, Math.max(0, dur - 0.25))
              : safeTargetSeconds;

          videoEl.currentTime = clamped;
        } catch {
          // ignore seek errors
        }
      }

      startPlayback();
    };

    if (safeTargetSeconds == null) {
      const rafId = requestAnimationFrame(seekAndPlay);

      return () => cancelAnimationFrame(rafId);
    }

    // If metadata is already available, seek immediately; otherwise wait for it.
    if (videoEl.readyState >= 1) {
      seekAndPlay();

      return undefined;
    }

    const onLoadedMetadata = () => {
      seekAndPlay();
    };

    videoEl.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      videoEl.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [videoType, mp4Src, seekSeconds, launchId]);

  const getVideoPlayback = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        setError(null);
        setFetchTimedOut(false);

        // If cached, use immediately to avoid spinner
        const cached = playbackCache.get(id);

        if (cached) {
          handlePlaybackResponse(cached);

          return;
        }

        const json = await fetchPlayback(id);

        handlePlaybackResponse(json);
      } catch (err) {
        // Clear any stuck inflight entry on failure/timeout so future attempts can retry
        playbackInflight.delete(id);

        if (
          err instanceof Error &&
          err.message &&
          err.message.toLowerCase().includes('timed out')
        ) {
          setFetchTimedOut(true);
        }

        let errorMessage: string;

        if (err instanceof Error && err.message) {
          errorMessage =
            err.message === VIDEO_FETCH_ERROR_MESSAGE
              ? err.message
              : l(err.message);
        } else {
          errorMessage = l('Failed to load session recording');
        }

        setError(errorMessage);
        setIsLoading(false);
      }
    },
    [handlePlaybackResponse, l]
  );

  const handleRetry = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setFetchTimedOut(false);
    getVideoPlayback(String(launchId)).catch(() => {
      // error handled within getVideoPlayback
    });
  }, [getVideoPlayback, launchId]);

  useEffect(() => {
    getVideoPlayback(String(launchId)).catch(() => {
      // Error is handled within getVideoPlayback via setError
    });
  }, [getVideoPlayback, launchId]);

  const requestFullscreen = (target: HTMLElement | null) => {
    if (!target) {
      return;
    }

    const anyTarget = target as HTMLElement & {
      requestFullscreen?: () => Promise<void> | void;
      webkitRequestFullscreen?: () => Promise<void> | void;
      mozRequestFullScreen?: () => Promise<void> | void;
      msRequestFullscreen?: () => Promise<void> | void;
    };

    let req: (() => Promise<void> | void) | undefined;

    if (typeof anyTarget.requestFullscreen === 'function') {
      req = anyTarget.requestFullscreen;
    } else if (typeof anyTarget.webkitRequestFullscreen === 'function') {
      req = anyTarget.webkitRequestFullscreen;
    } else if (typeof anyTarget.mozRequestFullScreen === 'function') {
      req = anyTarget.mozRequestFullScreen;
    } else if (typeof anyTarget.msRequestFullscreen === 'function') {
      req = anyTarget.msRequestFullscreen;
    }

    if (req) {
      const result = req.call(anyTarget);

      if (result && typeof result.catch === 'function') {
        result.catch(
          createDevRejectionHandler('SessionRecording: fullscreen request')
        );
      }
    }
  };

  useEffect(() => {
    const getFullscreenElement = (): Element | null => {
      const doc = document as Document & {
        webkitFullscreenElement?: Element | null;
        mozFullScreenElement?: Element | null;
        msFullscreenElement?: Element | null;
      };

      return (
        doc.fullscreenElement ??
        doc.webkitFullscreenElement ??
        doc.mozFullScreenElement ??
        doc.msFullscreenElement ??
        null
      );
    };

    const handleFsChange = () => {
      setIsFullscreen(Boolean(getFullscreenElement()));
    };

    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener(
      'webkitfullscreenchange' as unknown as string,
      handleFsChange
    );
    document.addEventListener(
      'mozfullscreenchange' as unknown as string,
      handleFsChange
    );
    document.addEventListener(
      'MSFullscreenChange' as unknown as string,
      handleFsChange
    );

    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener(
        'webkitfullscreenchange' as unknown as string,
        handleFsChange
      );
      document.removeEventListener(
        'mozfullscreenchange' as unknown as string,
        handleFsChange
      );
      document.removeEventListener(
        'MSFullscreenChange' as unknown as string,
        handleFsChange
      );
    };
  }, []);

  const exitFullscreen = () => {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
      mozCancelFullScreen?: () => Promise<void> | void;
      msExitFullscreen?: () => Promise<void> | void;
    };

    const hasCatch = (val: unknown): val is Promise<void> =>
      typeof val === 'object' &&
      val !== null &&
      typeof (val as Promise<void>).catch === 'function';

    let res: Promise<void> | void | undefined;

    if (typeof doc.exitFullscreen === 'function') {
      res = doc.exitFullscreen();
    } else if (typeof doc.webkitExitFullscreen === 'function') {
      res = doc.webkitExitFullscreen();
    } else if (typeof doc.mozCancelFullScreen === 'function') {
      res = doc.mozCancelFullScreen();
    } else if (typeof doc.msExitFullscreen === 'function') {
      res = doc.msExitFullscreen();
    }

    if (hasCatch(res)) {
      res.catch(createDevRejectionHandler('SessionRecording: exitFullscreen'));
    }
  };

  // NOTE: MP4 playback uses a normal React <video> element with a sanitized src.

  // Initialize Guacamole player for non-MP4 playback videos
  useEffect(() => {
    if (videoType === 'playback' && filename) {
      cancelledRef.current = false;

      const initializePlayer = async () => {
        try {
          // Wait for DOM elements to be ready
          const checkElements = () => {
            const player = document.getElementById('player');
            const display = document.getElementById('display');
            const playPause = document.getElementById('play-pause');
            const cancelSeek = document.getElementById('cancel-seek');
            const positionSlider = document.getElementById('position-slider');

            return (
              player &&
              display &&
              playPause &&
              cancelSeek !== null &&
              positionSlider
            );
          };

          // Load assets in parallel for faster initialization (idempotent)
          await Promise.all([
            loadCss(
              '/ECMv6/api/rdpgateway/playbackwithevents.css',
              'aag-playback-css'
            ),
            loadScript(
              '/ECMv6/api/rdpgateway/guacamole-common-js/all.js',
              'aag-guac-lib'
            ),
            loadScript('/ECMv6/api/rdpgateway/playback.js', 'aag-playback-lib'),
          ]);

          if (cancelledRef.current) {
            return;
          }

          // Wait for elements to be available using recursive promise chain
          // Optimized: Reduced max attempts from 50 (5s) to 15 (~1.5s) for faster failure
          // If assets are preloaded, elements should appear much faster; bail early if already ready
          const waitForElements = (attempt = 0): Promise<void> => {
            const isCancelled = cancelledRef.current;

            if (isCancelled) {
              return Promise.resolve();
            }

            // Check immediately first, then reduce wait time for faster detection
            if (checkElements() || attempt >= 15) {
              return Promise.resolve();
            }

            // Use shorter intervals (50ms) for faster detection when assets are preloaded
            const interval = attempt < 5 ? 50 : 100;

            return new Promise<void>((resolve) => {
              setTimeout(() => {
                resolve();
              }, interval);
            }).then(() => waitForElements(attempt + 1));
          };

          await waitForElements();

          const isCancelled = (): boolean => Boolean(cancelledRef.current);

          if (isCancelled()) {
            return;
          }

          if (!checkElements()) {
            setError(
              l('Failed to initialize player: Required DOM elements not found.')
            );

            return;
          }

          // Wait a brief moment after asset load for script execution
          await new Promise<void>((resolve) => {
            setTimeout(() => {
              resolve();
            }, 150);
          });

          if (isCancelled()) {
            return;
          }

          if (window.initPlayer) {
            // Sanitize filename before passing to initPlayer to prevent XSS
            // initPlayer constructs a URL with the filename, so we must ensure it's safe
            const sanitizedFilename = sanitizeFilename(filename);

            if (!sanitizedFilename) {
              setError(
                l(
                  'Invalid filename provided. Filename failed security validation.'
                )
              );

              return;
            }

            // Pass the sanitized filename - initPlayer will construct the stream URL
            window.initPlayer(sanitizedFilename);

            // Ensure play button has initial content (textContent avoids XSS-prone innerHTML - CWE-80)
            const playBtn = document.getElementById('play-pause');

            if (playBtn && !playBtn.textContent?.trim()) {
              playBtn.textContent = '\u25B6';
            }
          } else {
            setError(
              l('Failed to initialize player: initPlayer function not found.')
            );
          }
        } catch (err) {
          const isCancelled = cancelledRef.current;

          if (!isCancelled) {
            setError(
              err instanceof Error
                ? err.message
                : l('Failed to initialize session recording player.')
            );
          }
        }
      };

      initializePlayer().catch((err) => {
        const isCancelled = cancelledRef.current;

        if (!isCancelled) {
          setError(
            err instanceof Error
              ? err.message
              : l('Failed to initialize session recording player.')
          );
        }
      });

      return () => {
        cancelledRef.current = true;
      };
    }

    return undefined;
  }, [videoType, filename, l]);

  if (isLoading) {
    return (
      <Box
        className='Sod-SessionRecording-loading'
        data-start={launchStartDate ?? ''}>
        <Typography kind='body2'>{loadingText}</Typography>
        {fetchTimedOut && (
          <Box
            className='Sod-SessionRecording-error'
            style={{ marginTop: '0.5rem' }}>
            <Typography kind='body3' color='danger'>
              {l('Still loading. Please retry.')}
            </Typography>
            <Button kind='filled' size='small' onClick={handleRetry}>
              {l('Retry')}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        className='Sod-SessionRecording-error'
        data-start={launchStartDate ?? ''}>
        <Typography kind='body2' color='danger'>
          {error}
        </Typography>
        <Box style={{ marginTop: '0.5rem' }}>
          <Button kind='filled' size='small' onClick={handleRetry}>
            {l('Retry')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (!videoType) {
    return (
      <Box
        className='Sod-SessionRecording-unavailable'
        data-start={launchStartDate ?? ''}>
        <Typography kind='body2'>{unavailableText}</Typography>
      </Box>
    );
  }

  // MP4 playback (PAM-style): render a sanitized <video><source/></video> snippet
  if (videoType === 'past') {
    if (!mp4Src) {
      return (
        <Box
          className='Sod-SessionRecording-unavailable'
          data-start={launchStartDate ?? ''}>
          <Typography kind='body2'>{unavailableText}</Typography>
        </Box>
      );
    }

    return (
      <div className='Sod-SessionRecording-fixedBox'>
        <video
          ref={mp4VideoRef}
          id='usersessionvideo'
          width='100%'
          height='100%'
          controls
          muted
          playsInline
          preload='metadata'>
          <source src={mp4Src} type='video/mp4' />
          {l('Your browser does not support the video tag.')}
        </video>
      </div>
    );
  }

  // Guacamole playback markup expected by playback.js for non-MP4 videos
  // At this point, videoType must be 'playback' since we've handled other cases
  return (
    <div className='Sod-SessionRecording-guacBox' ref={guacContainerRef}>
      <div id='player' className='Sod-SessionRecording-guacPlayer'>
        <div id='display' className='Sod-SessionRecording-guacDisplay' />
        <div className='controls Sod-SessionRecording-guacControls'>
          <button
            id='play-pause'
            type='button'
            aria-label={l('Play or pause')}
          />
          <button
            id='cancel-seek'
            type='button'
            aria-label={l('Cancel seek')}
          />
          <div className='status'>
            <span id='position' />
            <input id='position-slider' type='range' min='0' defaultValue='0' />
            <span id='duration' />
          </div>
          <ButtonIcon
            size='medium'
            kind='ghost'
            className='Sod-SessionRecording-fullscreenBtn'
            onClick={() =>
              isFullscreen
                ? exitFullscreen()
                : requestFullscreen(guacContainerRef.current)
            }
            ariaLabel={
              isFullscreen ? l('Exit Fullscreen') : l('View Recording')
            }
            icon={
              <Icon
                kind={isFullscreen ? 'ExitFullScreen' : 'Fullscreen'}
                size='smallMedium'
                color='--color-foreground-inverse'
              />
            }
          />
        </div>
      </div>
    </div>
  );
}

SessionRecording.defaultProps = {
  launchStartDate: undefined,
  seekSeconds: undefined,
};

SessionRecordingWindows.defaultProps = {
  launchStartDate: undefined,
  seekSeconds: undefined,
};

export default SessionRecording;
